import { getContrast } from 'polished';
import { createGlobalStyle } from 'styled-components';

const isDark = (kioskColor: string) => {
  if (!kioskColor) {
    return false;
  }
  const contrast = getContrast(kioskColor, 'white');
  // contrast ratio from https://www.w3.org/TR/WCAG20-TECHS/G18.html
  return contrast > 4.5;
};

const GlobalStyles = createGlobalStyle<{ kioskEnabled: boolean; kioskColor: string }>`
  body.qr-scanner {
    background-color: transparent;
  }

  ion-modal.modal-full-screen {
    --width: 100%;
    --height: 100%;
  }

  @media (prefers-color-scheme: dark) {
    /*
     * Material Design Dark Theme
     * -------------------------------------------
     */
    .md body {
      --ion-item-background: transparent;
    }

    body {
      --ion-backdrop-color: grey;
    }
  }

  /* https://ionicframework.com/docs/theming/themes#stepped-color-generator */
  /* Dark mode color variables are already defined in ionic, but light mode aren't */
  :root {
    --ion-background-color: #ffffff;
    --ion-background-color-rgb: 255,255,255;

    --ion-text-color: #000000;
    --ion-text-color-rgb: 0,0,0;

    --ion-text-color-step-50: #0d0d0d;
    --ion-text-color-step-100: #1a1a1a;
    --ion-text-color-step-150: #262626;
    --ion-text-color-step-200: #333333;
    --ion-text-color-step-250: #404040;
    --ion-text-color-step-300: #4d4d4d;
    --ion-text-color-step-350: #595959;
    --ion-text-color-step-400: #666666;
    --ion-text-color-step-450: #737373;
    --ion-text-color-step-500: #808080;
    --ion-text-color-step-550: #8c8c8c;
    --ion-text-color-step-600: #999999;
    --ion-text-color-step-650: #a6a6a6;
    --ion-text-color-step-700: #b3b3b3;
    --ion-text-color-step-750: #bfbfbf;
    --ion-text-color-step-800: #cccccc;
    --ion-text-color-step-850: #d9d9d9;
    --ion-text-color-step-900: #e6e6e6;
    --ion-text-color-step-950: #f2f2f2;

    --ion-background-color-step-50: #f2f2f2;
    --ion-background-color-step-100: #e6e6e6;
    --ion-background-color-step-150: #d9d9d9;
    --ion-background-color-step-200: #cccccc;
    --ion-background-color-step-250: #bfbfbf;
    --ion-background-color-step-300: #b3b3b3;
    --ion-background-color-step-350: #a6a6a6;
    --ion-background-color-step-400: #999999;
    --ion-background-color-step-450: #8c8c8c;
    --ion-background-color-step-500: #808080;
    --ion-background-color-step-550: #737373;
    --ion-background-color-step-600: #666666;
    --ion-background-color-step-650: #595959;
    --ion-background-color-step-700: #4d4d4d;
    --ion-background-color-step-750: #404040;
    --ion-background-color-step-800: #333333;
    --ion-background-color-step-850: #262626;
    --ion-background-color-step-900: #191919;
    --ion-background-color-step-950: #0d0d0d;
  }


  ${({ kioskEnabled, kioskColor }) =>
    kioskEnabled &&
    kioskColor &&
    `
    body, .md body {
      --ion-toolbar-background:${kioskColor};
      --ion-tab-bar-background:${kioskColor};
      --ion-color-primary:${kioskColor};
      --ion-color-primary-shade:${kioskColor};
    }  
  `}
  
  ${({ kioskEnabled, kioskColor }) =>
    kioskEnabled &&
    isDark(kioskColor) &&
    `
      .md body {
        --ion-border-color: #dddddd;
        .toolbar-searchbar {
          --background: var(--ion-color-step-50);
        }
      }

      .ios body, .md body {
        --ion-toolbar-color: #fff;
        --ion-color-primary-contrast: #fff;
        --ion-tab-bar-color: var(--ion-item-background);
        --ion-tab-bar-color-selected: var(--ion-item-background);

        --ion-background-color: #ffffff;
        --ion-background-color-rgb: 255,255,255;
        --ion-text-color: #000000;
        --ion-text-color-rgb: 0,0,0;

        --ion-item-background: #ffffff;
        --ion-card-background: #e3e3e2;

        --ion-background-color-step-50: #f2f2f2;
        --ion-background-color-step-100: #e6e6e6;
        --ion-background-color-step-150: #d9d9d9;
        --ion-background-color-step-200: #cccccc;
        --ion-background-color-step-250: #bfbfbf;
        --ion-background-color-step-300: #b3b3b3;
        --ion-background-color-step-350: #a6a6a6;
        --ion-background-color-step-400: #999999;
        --ion-background-color-step-450: #8c8c8c;
        --ion-background-color-step-500: #808080;
        --ion-background-color-step-550: #737373;
        --ion-background-color-step-600: #666666;
        --ion-background-color-step-650: #595959;
        --ion-background-color-step-700: #4d4d4d;
        --ion-background-color-step-750: #404040;
        --ion-background-color-step-800: #333333;
        --ion-background-color-step-850: #262626;
        --ion-background-color-step-900: #191919;
        --ion-background-color-step-950: #0d0d0d;
      }
      .ios ion-modal {
        /* custom override, not from ionic light/dark mode */
        --ion-background-color: #ffffff;
        --ion-toolbar-background: var(--ion-color-step-150);
        --ion-toolbar-border-color: var(--ion-color-step-250);
        /* custom override, not from ionic light/dark mode */
        color: #000000;
      }
      `}

    ${({ kioskEnabled, kioskColor }) =>
      kioskEnabled &&
      !isDark(kioskColor) &&
      `
      /*
      * iOS Dark Theme
      * -------------------------------------------
      */

      .ios body {
        --ion-toolbar-color: #000;
        --ion-color-primary-contrast: #000;
        --ion-tab-bar-color: var(--ion-item-background);
        --ion-tab-bar-color-selected: var(--ion-item-background);

        --ion-background-color: #000000;
        --ion-background-color-rgb: 0,0,0;

        --ion-text-color: #ffffff;
        --ion-text-color-rgb: 255,255,255;

        --ion-item-background: #000000;
        --ion-card-background: #1c1c1d;

        --ion-color-step-50: #0d0d0d;
        --ion-color-step-100: #1a1a1a;
        --ion-color-step-150: #262626;
        --ion-color-step-200: #333333;
        --ion-color-step-250: #404040;
        --ion-color-step-300: #4d4d4d;
        --ion-color-step-350: #595959;
        --ion-color-step-400: #666666;
        --ion-color-step-450: #737373;
        --ion-color-step-500: #808080;
        --ion-color-step-550: #8c8c8c;
        --ion-color-step-600: #999999;
        --ion-color-step-650: #a6a6a6;
        --ion-color-step-700: #b3b3b3;
        --ion-color-step-750: #bfbfbf;
        --ion-color-step-800: #cccccc;
        --ion-color-step-850: #d9d9d9;
        --ion-color-step-900: #e6e6e6;
        --ion-color-step-950: #f2f2f2;
      }

      .ios ion-modal {
        /* custom override, not from ionic light/dark mode */
        --ion-background-color: #000000;
        --ion-toolbar-background: var(--ion-color-step-150);
        --ion-toolbar-border-color: var(--ion-color-step-250);
        /* custom override, not from ionic light/dark mode */
        color: #ffffff;
      }


      /*
      * Material Design Dark Theme
      * -------------------------------------------
      */

      .md body {        
        .toolbar-searchbar {
          --background: var(--ion-color-step-50);
        }
        --ion-toolbar-color: #000;
        --ion-color-primary-contrast: #000;
        --ion-tab-bar-color: var(--ion-item-background);
        --ion-tab-bar-color-selected: var(--ion-item-background);

        --ion-background-color: #121212;
        --ion-background-color-rgb: 18,18,18;

        --ion-text-color: #ffffff;
        --ion-text-color-rgb: 255,255,255;

        --ion-item-background: #1e1e1e;

        --ion-border-color: #222222;
        --ion-item-background: #000000;

        --ion-card-background: #e3e3e2;
        
        --ion-color-step-50: #1e1e1e;
        --ion-color-step-100: #2a2a2a;
        --ion-color-step-150: #363636;
        --ion-color-step-200: #414141;
        --ion-color-step-250: #4d4d4d;
        --ion-color-step-300: #595959;
        --ion-color-step-350: #656565;
        --ion-color-step-400: #717171;
        --ion-color-step-450: #7d7d7d;
        --ion-color-step-500: #898989;
        --ion-color-step-550: #949494;
        --ion-color-step-600: #a0a0a0;
        --ion-color-step-650: #acacac;
        --ion-color-step-700: #b8b8b8;
        --ion-color-step-750: #c4c4c4;
        --ion-color-step-800: #d0d0d0;
        --ion-color-step-850: #dbdbdb;
        --ion-color-step-900: #e7e7e7;
        --ion-color-step-950: #f3f3f3;
      }
    `}`;

export default GlobalStyles;
